import { useEffect, useRef, useState, type ChangeEvent } from 'react';

import type { LocalDatabase } from '../db/powersync.js';
import { useCreateBlock } from '../db/use-create-block.js';
import { t } from '../i18n/es.js';
import { useSession } from '../session.jsx';
import { useSetViewHeader } from '../shell/AppLayout.jsx';

/**
 * Test screen of F0 (f0-mobile-pwa, docs/07 §8.1): camera, microphone and keyboard dictation
 * from the browser and from the installed PWA on iPhone. Nothing is uploaded or stored as an
 * asset (F3/F4); only the dictated text becomes a block, through the domain command.
 */
export function CapturePage() {
  const { user, local } = useSession();
  const setHeader = useSetViewHeader();
  useEffect(() => {
    setHeader({ title: t.capture.title });
  }, [setHeader]);
  if (!user || !local) return null;
  return (
    <div className="page">
      <p className="muted">{t.capture.intro}</p>
      <Environment local={local} />
      <PhotoCapture />
      <CameraStream />
      <AudioCapture />
      <Dictation local={local} userId={user.id} />
    </div>
  );
}

/** Installed (standalone) or browser tab, storage persistence and user agent, for the matrix. */
function Environment({ local }: { local: LocalDatabase }) {
  const standalone =
    (navigator as Navigator & { standalone?: boolean }).standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;
  return (
    <section className="capture-section" aria-labelledby="env-title">
      <h2 id="env-title">{t.capture.environment}</h2>
      <p data-testid="capture-mode" data-standalone={standalone}>
        {standalone ? t.capture.installed : t.capture.browserTab} ·{' '}
        {t.capture.persisted(local.persisted)} · {t.sync.vfs(local.vfs.name)}
      </p>
      <p className="muted" style={{ overflowWrap: 'anywhere' }}>
        {navigator.userAgent}
      </p>
    </section>
  );
}

function describeError(error: unknown): string {
  const name = error instanceof Error ? error.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return t.capture.permissionDenied;
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return t.capture.notFound;
  if (name === 'NotSupportedError' || name === 'TypeError') return t.capture.notSupported;
  return t.capture.error(error instanceof Error ? error.message : String(error));
}

interface PhotoInfo {
  url: string;
  width: number;
  height: number;
  orientedWidth: number;
  orientedHeight: number;
  kb: number;
  type: string;
}

/** First way: the native camera through `<input type="file" capture>`; also the photo library. */
function PhotoCapture() {
  const [photo, setPhoto] = useState<PhotoInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => () => (photo ? URL.revokeObjectURL(photo.url) : undefined), [photo]);

  async function handleFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError(null);
    try {
      // Raw pixels vs. EXIF orientation applied (portrait photos of iPhone are stored rotated).
      const raw = await createImageBitmap(file, { imageOrientation: 'none' });
      const oriented = await createImageBitmap(file, { imageOrientation: 'from-image' });
      setPhoto({
        url: URL.createObjectURL(file),
        width: raw.width,
        height: raw.height,
        orientedWidth: oriented.width,
        orientedHeight: oriented.height,
        kb: Math.round(file.size / 1024),
        type: file.type,
      });
      raw.close();
      oriented.close();
    } catch (err) {
      setError(describeError(err));
    }
  }

  return (
    <section className="capture-section" aria-labelledby="photo-title">
      <h2 id="photo-title">{t.capture.photo.title}</h2>
      <div className="row">
        <label className="file-button">
          {t.capture.photo.take}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => void handleFile(e)}
            data-testid="photo-capture"
          />
        </label>
        <label className="file-button">
          {t.capture.photo.pick}
          <input type="file" accept="image/*" onChange={(e) => void handleFile(e)} />
        </label>
      </div>
      {error ? <p className="error">{error}</p> : null}
      {photo ? (
        <figure style={{ margin: 0, display: 'grid', gap: '0.5rem' }}>
          {/* Details above the preview: a tall photo pushes anything below it off screen. */}
          <figcaption data-testid="photo-details">
            <strong>
              {t.capture.photo.details(photo.width, photo.height, photo.kb, photo.type)}
            </strong>
            {photo.orientedWidth !== photo.width ? (
              <> · {t.capture.photo.oriented(photo.orientedWidth, photo.orientedHeight)}</>
            ) : null}
          </figcaption>
          <img className="preview" src={photo.url} alt="" />
        </figure>
      ) : null}
    </section>
  );
}

/** Second way: live camera through `getUserMedia` (checks availability in the installed app). */
function CameraStream() {
  const video = useRef<HTMLVideoElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const element = video.current;
    if (!element) return;
    element.srcObject = stream;
    if (!stream) return;
    const onMeta = () => setSize({ w: element.videoWidth, h: element.videoHeight });
    element.addEventListener('loadedmetadata', onMeta);
    void element.play().catch(() => undefined);
    return () => {
      element.removeEventListener('loadedmetadata', onMeta);
      stream.getTracks().forEach((track) => track.stop());
    };
  }, [stream]);

  async function open() {
    setError(null);
    try {
      const media = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
      setStream(media);
    } catch (err) {
      setError(describeError(err));
    }
  }

  return (
    <section className="capture-section" aria-labelledby="camera-title">
      <h2 id="camera-title">getUserMedia</h2>
      <div className="row">
        {stream ? (
          <button
            type="button"
            onClick={() => {
              setStream(null);
              setSize(null);
            }}
          >
            {t.capture.photo.closeCamera}
          </button>
        ) : (
          <button type="button" onClick={() => void open()}>
            {t.capture.photo.openCamera}
          </button>
        )}
        {size ? (
          <span data-testid="camera-size">{t.capture.photo.cameraOn(size.w, size.h)}</span>
        ) : null}
      </div>
      {error ? <p className="error">{error}</p> : null}
      <video ref={video} playsInline muted autoPlay hidden={!stream} />
    </section>
  );
}

const RECORD_SECONDS = 10;
const MIME_CANDIDATES = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg'];

interface AudioInfo {
  url: string;
  kb: number;
  type: string;
  seconds: string;
}

/** Microphone through `MediaRecorder`: 10 s (or stop earlier), then playback and details. */
function AudioCapture() {
  const recorder = useRef<MediaRecorder | null>(null);
  const [elapsed, setElapsed] = useState<number | null>(null);
  const [audio, setAudio] = useState<AudioInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const supported = typeof MediaRecorder !== 'undefined';

  useEffect(() => () => (audio ? URL.revokeObjectURL(audio.url) : undefined), [audio]);

  async function start() {
    setError(null);
    setAudio(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      const mimeType = MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type));
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const chunks: Blob[] = [];
      const started = Date.now();
      rec.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };
      rec.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        const blob = new Blob(chunks, { type: rec.mimeType });
        const seconds = ((Date.now() - started) / 1000).toFixed(1);
        setAudio({
          url: URL.createObjectURL(blob),
          kb: Math.round(blob.size / 1024),
          type: blob.type,
          seconds: `${seconds} s`,
        });
        setElapsed(null);
        recorder.current = null;
      };
      recorder.current = rec;
      rec.start();
      setElapsed(0);
      const tick = setInterval(() => {
        const s = Math.round((Date.now() - started) / 1000);
        setElapsed(s);
        if (s >= RECORD_SECONDS) {
          clearInterval(tick);
          if (rec.state === 'recording') rec.stop();
        }
      }, 500);
      rec.addEventListener('stop', () => clearInterval(tick), { once: true });
    } catch (err) {
      setError(describeError(err));
    }
  }

  return (
    <section className="capture-section" aria-labelledby="audio-title">
      <h2 id="audio-title">{t.capture.audio.title}</h2>
      {!supported ? <p className="error">{t.capture.audio.unsupported}</p> : null}
      <div className="row">
        {elapsed === null ? (
          <button type="button" onClick={() => void start()} disabled={!supported}>
            {t.capture.audio.record}
          </button>
        ) : (
          <button type="button" onClick={() => recorder.current?.stop()}>
            {t.capture.audio.stop}
          </button>
        )}
        {elapsed !== null ? (
          <span aria-live="polite">{t.capture.audio.recording(elapsed)}</span>
        ) : null}
      </div>
      {error ? <p className="error">{error}</p> : null}
      {audio ? (
        <>
          <audio controls src={audio.url} />
          <p data-testid="audio-details">
            {t.capture.audio.details(audio.seconds, audio.kb, audio.type)}
          </p>
        </>
      ) : null}
    </section>
  );
}

/** Keyboard dictation: plain text field; the text is saved as a normal capture. */
function Dictation({ local, userId }: { local: LocalDatabase; userId: string }) {
  const { ready, create } = useCreateBlock(local, userId);
  const [text, setText] = useState('');
  const [saved, setSaved] = useState<string | null>(null);

  async function save() {
    const title = text.trim();
    if (!title) return;
    await create(title);
    setText('');
    setSaved(title);
  }

  return (
    <section className="capture-section" aria-labelledby="dictation-title">
      <h2 id="dictation-title">{t.capture.dictation.title}</h2>
      <p className="muted">{t.capture.dictation.hint}</p>
      <textarea
        aria-label={t.capture.dictation.placeholder}
        placeholder={t.capture.dictation.placeholder}
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="row">
        <button type="button" onClick={() => void save()} disabled={!ready || !text.trim()}>
          {t.capture.dictation.save}
        </button>
        {saved ? (
          <span data-testid="dictation-saved">{t.capture.dictation.saved(saved)}</span>
        ) : null}
      </div>
    </section>
  );
}
